// React Router
import { useNavigate, useParams } from "react-router-dom";

// React Core
import { useContext, useEffect, useMemo, useRef, useState } from "react";

// Assets
import downloadSvg from '../assets/general/download.svg';
import printSvg from '../assets/general/print.svg';
import mapSvg from '../assets/general/map.svg';
import layerSvg from '../assets/general/layer.svg';
import { UnfoldVertical, FoldVertical } from "lucide-react";

// Custom Hooks
import useFetchData from "../hooks/useFetchData";

// Utilities
import { categorizeData, getSvgMap } from "../utils/dashboardUtils";
import { handleExcelDownload } from "../utils/excelExportModule";

// Contexts
import MetricCategoriesContext from "../context/MetricCategoriesContext";
import LayersContext from "../context/LayersContext";
import MetadataContext from "../context/MetadataContext";
import { useOrgContext } from "../context/OrgIdContext";

// Core Components
import DownloadLoading from "../components/general/loading/DownloadLoading";

// Modals
import LegendMetricModal from "../components/modals/LegendMetricModal";
import LayersModal from "../components/layers/LayersModal";
import PDFModal from "../components/modals/pdfs/PDFModal";
import MappableMetricsModal from "../components/modals/mappableModal/MappableMetricsModal";
import ClearMapSelectionModal from "../components/modals/ClearMapSelectionModal";
import MapItemDetailsModal from "../components/modals/MapItemDetailsModal";
import ProgramPDFModal from "../components/modals/pdfs/ProgramPDFModal";

// Panels
import LayerPanel from "../components/layers/LayerPanel";
import EducationMetrics from "../components/panels/EducationMetrics";
import LivabilityMetrics from "../components/panels/LivabilityMetrics";
import EconomyMetrics from "../components/panels/EconomyMetrics";
import AccessibilityMetrics from "../components/panels/AccessibilityMetrics";
import DemographicMetrics from "../components/panels/DemographicMetrics";

// Components
import TabMenu from "../components/general/TabMenu";

// Skeletons
import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';
import MetricCardSkeleton from "../components/skeletons/MetricCardSkeleton";
import TabMenuSkeleton from "../components/skeletons/TabMenuSkeleton";
import { tabColors } from "../utils/helpers";
import { DashboardColorContext } from "../context/DashboardColorContext";


const Dashboard = () => {
    const isIframe = window.self !== window.top;
    const navigate = useNavigate();
    const { tab } = useParams<{ tab: string }>();
    const { orgId } = useOrgContext();
    const { layers, loading: layersLoading } = useContext(LayersContext);
    const { metricCategories, loading: categoriesLoading } = useContext(MetricCategoriesContext);
    // Fetch metric metadata for the current org
    const { metadata, loading: metadataLoading } = useContext(MetadataContext);
    const prevViewRef = useRef<string | null>(null);

    // Dashboard state
    const [expandedSection, setExpandedSection] = useState<"metrics" | "layerPanel" | null>(null);

    // Message prop state
    const [currentTab, setCurrentTab] = useState<string>("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [legendUrl, setLegendUrl] = useState('');
    const [selectedArea, setSelectedArea] = useState('');
    const [siteUrl, setSiteUrl] = useState('');
    const [legendId, setLegendId] = useState(0);

    // Modal state
    const [isLegendModalOpen, setIsLegendModalOpen] = useState(false);
    const [isMappableModalOpen, setIsMappableModalOpen] = useState(false);
    const [isLayersModalOpen, setIsLayersModalOpen] = useState(false);
    const [isProgramPDFModalOpen, setIsProgramPDFModalOpen] = useState(false);

    // Excel state
    const [shouldDownloadExcel, setShouldDownloadExcel] = useState(false);
    const [showDownloadLoading, setShowDownloadLoading] = useState(false);

    // Map item details modal state
    const [mapItemDetailModalOpen, setMapItemDetailModalOpen] = useState(false);
    const [mapItemParams, setMapItemParams] = useState({ layerId: null, id: null });    

    // Geographic levels and exclusion lists defined for excel (will vary by project)
    const geographicLevels = [
        ...new Set(metadata?.flatMap((metric) => 
          metric.versions.map((version) => version.level_of_detail)
        ))
    ];

    const summaryExclusionList: string[] = [];
    const disaggregationExclusionList: string[] = [];
    const geographyExclusionList: string[] = [];

    const { indicators, loading, dashboardView, geoDetails, geoTag, selectionActive, lodMissing } = useFetchData(currentTab || '');

    const tabs = metricCategories;

    // Loading flags
    const showMetricContent = !(loading || categoriesLoading || metadataLoading);
    const noMetrics = showMetricContent && !loading && indicators.length === 0;

    // #region React useEffects
    useEffect(() => {
        if (!isIframe && tabs.length > 0) {
            if (tab && tabs.includes(tab)) {
                setCurrentTab(tab.toLowerCase());
            } else if (!currentTab) {
                setCurrentTab(tabs[0]);
            }
        }
    }, [tab, isIframe, tabs]);

    useEffect(() => {
        if (isIframe && dashboardView && tabs.length > 0) {
            // Check if dashboardView matches a layer_name and add it to tabs
            const foundLayer = layers.find((layer) => layer.layer_name.toLowerCase() === dashboardView.toLowerCase());
    
            // If foundLayer is present, update the tabs
            if (foundLayer && !tabs.includes(dashboardView)) {
                const updatedTabs = [...tabs, dashboardView];
                setCurrentTab(updatedTabs.find((t) => t.toLowerCase() === dashboardView.toLowerCase()) || updatedTabs[0]);
            } else {
                setCurrentTab(tabs.find((t) => t.toLowerCase() === dashboardView.toLowerCase()) || tabs[0]);
            }
        } else {
            // When not in iframe, just ensure the currentTab is set based on the `tab` param
            setCurrentTab(tabs.find((t) => t.toLowerCase() === currentTab?.toLowerCase()) || tabs[0]);
        }
    }, [dashboardView, isIframe, tabs, layers]);

    // Combine data from all sources
    const allDataAvailable = useMemo(() => {
        return !loading && indicators;
    }, [loading, indicators]);


    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data.message === 'pdf') {
                setPdfUrl(event.data.params.url);
                setLogoUrl(event.data.params.logoUrl || '');
                setLegendUrl(event.data.params.legendUrl || '');
                setSelectedArea(event.data.params.selectedArea || '');
                setSiteUrl(event.data.params.siteUrl || '');
                setIsModalOpen(true);
            
                // Send message to expand
                window.parent.postMessage({ 
                    message: "expand",
                    mode: "fullscreen"
                }, '*');
                console.log('expand message sent');
            } else if (event.data.message === 'metric-modal') {

                // Legend metadata info icon event
                console.log('Message received from parent app inside legend metric modal:', event.data);
                setLegendId(event.data.params.id);
            } else if (event.data.message === 'excel') {
                // Get selected area
                const sArea = event.data?.params?.selectedArea;
                // Show loading modal
                setShowDownloadLoading(true);
                
                if (!loading) {
                    // Execute excel download script
                    handleExcelDownload(orgId, geoDetails, geoTag, metricCategories, sArea, summaryExclusionList, disaggregationExclusionList, geographyExclusionList, geographicLevels, 'Base Download').finally(() => {
                        setShowDownloadLoading(false);
                    });
                } else {
                    console.log('Data is still loading, will attempt to download Excel later.');
                    setShouldDownloadExcel(true); // Set flag to retry download
                }
            } else if (event.data.message === 'pointLayerDetails') {
                console.log('point layer message:', event.data);
                const { layerId, id } = event.data.params || {};
                if (layerId && id) {
                    setMapItemParams({ layerId, id });
                    setMapItemDetailModalOpen(true);
                }
            }
        };
    
        window.addEventListener('message', handleMessage);
    
        return () => {
          window.removeEventListener('message', handleMessage);
        };
    }, [allDataAvailable, geoDetails]);

    useEffect(() => {
        if (!loading && shouldDownloadExcel) {
            setShowDownloadLoading(true);
            handleExcelDownload(orgId, geoDetails, geoTag, metricCategories, selectedArea, summaryExclusionList, disaggregationExclusionList, geographyExclusionList, geographicLevels, 'Base Download').finally(() => {
                setShouldDownloadExcel(false); 
                setShowDownloadLoading(false);
            });
        }
    }, [loading, shouldDownloadExcel, geoDetails]);

    // Close map item detail modal when dashboard panel changes
    useEffect(() => {
        if (prevViewRef.current !== null && prevViewRef.current !== dashboardView) {
            setMapItemDetailModalOpen(false);
        }
        // Update dashboard view ref
        prevViewRef.current = dashboardView;
    }, [dashboardView]);

    //#endregion

    //#region Event Handlers
    const closeLegendModal = () => {
        setIsLegendModalOpen(false);
        setLegendId(-1);
    };

    const closeModal = (e: React.MouseEvent<HTMLButtonElement>) => {
        let origin = '*';

        e.preventDefault();
        setIsModalOpen(false);
        // Send message to expand
        window.parent.postMessage({ message: "expand" }, origin);
        console.log('expand message sent');
    };

    const handleDownloadNoIframe = () => {
        // Show loading modal
        setShowDownloadLoading(true);
                
        if (!loading) {
            // Execute excel download script
            handleExcelDownload(orgId, geoDetails, geoTag, metricCategories, selectedArea, summaryExclusionList, disaggregationExclusionList, geographyExclusionList, geographicLevels, 'Base Download').finally(() => {
                setShowDownloadLoading(false);
            });
        }
    };

    const handlePrintNoIframe = () => {
        setPdfUrl('');
        setLogoUrl('');
        setLegendUrl('');
        setSelectedArea('');
        setSiteUrl('');
        setIsModalOpen(true);
    };

    const handleShowLayers = () => {
        setIsLayersModalOpen((prevState) => !prevState);
    };

    const handleMappableModal = () => {
        setIsMappableModalOpen(true);
    }

    const closeMappableModal = () => {
        setIsMappableModalOpen(false);
    }

    const closeMapItemModal = () => setMapItemDetailModalOpen(false);

    // Programmatic PDF Modal Handlers
    const openProgramPDFModal = () => {
        setIsProgramPDFModalOpen(true);
        window.parent.postMessage(
            {
            message: "expand",
            mode: "fullscreen",
            },
            "*"
        );
        console.log('expand message sent');
    };

    const closeProgramPDFModal = () => {
        setIsProgramPDFModalOpen(false);
        let origin = '*';
        // Send message to expand
        window.parent.postMessage({ message: "expand" }, origin);
        console.log('expand message sent');
    };

    const toggleMetricsSize = () => {
        setExpandedSection(prev => (prev === "metrics" ? null : "metrics"));
    };

    const toggleLayerPanelSize = () => {
        setExpandedSection(prev => (prev === "layerPanel" ? null : "layerPanel"));
    };
    //#endregion

    const HEADER_HEIGHT = 36; // px
    const TAB_MENU_HEIGHT = 86; // px

    const sectionHeight = expandedSection
    ? expandedSection === "metrics"
        ? { metrics: 0.75, layer: 0.25 }
        : { metrics: 0.25, layer: 0.75 }
    : { metrics: 0.5, layer: 0.5 };

    const categorizedData = categorizeData(indicators);

    // renderMetrics - generate current current tab (if non-metric show layer panel, otherwise show the mapped metric panel component)
    const renderMetrics = (tab: string) => {
        if (lodMissing) return <ClearMapSelectionModal />;

        // Handle "non-metric" tabs by showing only LayerPanel
        if (tab === "2025 Employment Lands" && !layersLoading) {
            return (
                <LayerPanel sharedView={false} dashboardView={tab} onPrintClick={openProgramPDFModal} />
            );
        }

        const color = tabColors[tab] || "#000";

        // Map tab names to corresponding components
        const metricsComponents: Record<string, JSX.Element> = {
            Livability: <LivabilityMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />,
            Economy: <EconomyMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />,
            Education: <EducationMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />,
            Access: <AccessibilityMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />,
            People: <DemographicMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />
        };

        return (
            <>
                <div>
                    {/* Show loading skeleton */}
                    {!showMetricContent ? (
                        <>
                            <div className="grid grid-cols-2 xxs:grid-cols-3 w-full mx-2">
                                <MetricCardSkeleton/>
                                <MetricCardSkeleton/>
                                <MetricCardSkeleton/>
                                <MetricCardSkeleton/>
                                <MetricCardSkeleton/>
                                <MetricCardSkeleton/>
                            </div>
                        </>
                    ) : noMetrics ? (
                        <div className="flex items-center justify-center text-sm text-color-cai-dashboard">
                            No metrics available for {tab}.
                        </div>
                    ) : ( 
                        <DashboardColorContext.Provider value={color}>
                            {metricsComponents[tab]}
                        </DashboardColorContext.Provider>
                    )}
                </div>
            </>
        );
    };

    // Set active tab to current tab if present
    const activeTab = currentTab?.toLowerCase() || tabs[0];

    return (
        <div className={`${!isIframe ? 'max-w-[456px] mx-auto print:max-w-full' : ''}`}>
            {/* Functional Icons for Non-iframe View */}
            {!isIframe && (
                <div className="my-2 mx-5 flex items-center justify-end gap-3">
                    {layers.length > 0 && <img src={layerSvg} alt="layer icon" title="Show metric layers" className="w-5 h-5 hover:cursor-pointer" onClick={() => { handleShowLayers() }}/> }
                    <img src={printSvg} alt="print icon" title="Print PDF" className="w-5 h-5 hover:cursor-pointer" onClick={() => { handlePrintNoIframe() }}/>
                    <img src={downloadSvg} alt="download icon" title="Download Excel" className="w-5 h-5 hover:cursor-pointer" onClick={() => { handleDownloadNoIframe() }}/>
                    <img src={mapSvg} alt="map icon" title="Show Mappable Metrics" className="w-5 h-5 hover:cursor-pointer" onClick={() => { handleMappableModal() }}/>
                </div>
            )}

            {/* Main dashboard container */}
            <div className="no-print h-screen flex flex-col overflow-hidden">
                {/* Metrics Section Header */}
                {tabs.length > 0 ?
                    <div className="sticky top-0 z-10 px-4 pt-2 bg-white" style={{ height: HEADER_HEIGHT }}>
                        <header className="relative bg-dark-gg px-2 py-1 flex items-center justify-center">
                            <h2 className="text-sm font-semibold text-white text-center flex-grow">Place Data</h2>
                            <button 
                                onClick={toggleMetricsSize} 
                                className="absolute right-2 text-white hover:bg-white/20 rounded-full transition-colors" 
                                title={expandedSection === "layerPanel" ? "Collapse" : "Expand"}
                            >
                                {expandedSection === "metrics" ? <FoldVertical className="w-5 h-5"/> : <UnfoldVertical className="w-5 h-5"/>}
                            </button>
                        </header>
                    </div>
                :
                    <div className="px-4">
                        <Skeleton height={28} width="100%" /> 
                    </div>
                }
                {/* Tab menu for dashboard categories */}
                <div className="sticky top-0 z-50 bg-white" style={{ height: TAB_MENU_HEIGHT }}>
                    {tabs.length > 0 
                    ? <TabMenu
                        tabs={tabs}
                        activeTab={activeTab}
                        setCurrentTab={setCurrentTab}
                        navigate={navigate}
                    />
                    : <TabMenuSkeleton/>
                    }
                </div>

                {/* Render current dashboard view */}
                {/* Metrics Section */}
                {!isModalOpen && !isProgramPDFModalOpen && (
                    <div className="flex-shrink-0 overflow-y-auto px-4 pb-6 border-b" style={{height: `calc(${sectionHeight.metrics * 100}vh - ${HEADER_HEIGHT + TAB_MENU_HEIGHT}px)`}}>
                        {renderMetrics(currentTab)}
                    </div>
                )}

                {/* Layer Panel Section */}
                <div className="flex-grow overflow-y-auto px-4" style={{ height: `${sectionHeight.layer * 100}vh` }}>
                    <LayerPanel
                    sharedView={true}
                    dashboardView={'tab'}
                    onPrintClick={openProgramPDFModal}
                    expandedSection={expandedSection}
                    toggleSize={toggleLayerPanelSize}
                    />
                </div>
            </div>

            {/* Modals */}
            <LegendMetricModal
                id={legendId}
                isOpen={isLegendModalOpen}
                onClose={closeLegendModal}
                geoDetails={geoDetails}
                svg={getSvgMap(legendId)}
                geoTag={geoTag}
                selectionActive={selectionActive}
            />

            {isModalOpen && <PDFModal
                isOpen={isModalOpen}
                onClose={closeModal}
                geoDetails={geoDetails}
                geoTag={geoTag}
                metadata={metadata}
                pdfUrl={pdfUrl}
                logoUrl={logoUrl}
                legendUrl={legendUrl}
                selectedArea={selectedArea}
                siteUrl={siteUrl}
                selectionActive={selectionActive}
            />}

            {isMappableModalOpen && <MappableMetricsModal
                isOpen={isMappableModalOpen}
                onClose={closeMappableModal}
            />}

            {isLayersModalOpen && <LayersModal isOpen={isLayersModalOpen} onClose={handleShowLayers} />}

            {mapItemDetailModalOpen && <MapItemDetailsModal layerId={mapItemParams?.layerId} pointId={mapItemParams?.id} isOpen={mapItemDetailModalOpen} onClose={closeMapItemModal}/> }

            {isProgramPDFModalOpen && (
                <ProgramPDFModal
                isOpen={isProgramPDFModalOpen}
                onClose={closeProgramPDFModal}
                layers={layers}
                geoDetails={geoDetails}
                geoTag={geoTag}
                pdfUrl={pdfUrl}
                logoUrl={logoUrl}
                selectedArea={selectedArea}
                siteUrl={siteUrl}
                />
            )}

            {showDownloadLoading &&
                <DownloadLoading/>
            }
        </div>
    );
}

export default Dashboard;